import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Crown, Mail, Lock, Eye, EyeOff, User, Coins, Gift, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [ageVerified, setAgeVerified] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!ageVerified) {
      toast.error(t('auth.mustBe18'));
      return;
    }

    setLoading(true);

    try {
      await register(email, password, name);
      toast.success(t('auth.accountCreated'));
      navigate('/dashboard');
    } catch (error) {
      console.error('Registration failed:', error);
      toast.error(error.response?.data?.detail || t('auth.registrationFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo with Glamour */}
          <Link to="/" className="flex items-center gap-2 mb-12">
            <Crown className="w-8 h-8 text-[#D4AF37] drop-shadow-[0_0_8px_rgba(212,175,55,0.5)]" />
            <span className="text-2xl">
              <span className="brand-ella text-white">Ella</span>{' '}
              <span className="brand-gold text-3xl">Gold</span>
            </span>
          </Link>

          {/* Header */}
          <h1 className="text-3xl sm:text-4xl font-medium tracking-tight mb-3">
            {t('auth.becomeMember').split(' ')[0]} <span className="brand-gold text-4xl">{t('auth.becomeMember').split(' ').slice(1).join(' ') || 'member'}</span>
          </h1>
          <p className="text-gray-400 mb-4">
            {t('auth.accessExclusive')}
          </p>

          {/* Token System Info Box */}
          <div className="p-4 mb-6 rounded-xl bg-gradient-to-r from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/30">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                <Coins className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <h3 className="font-medium text-[#D4AF37] mb-1">{t('auth.tokenSystemTitle')}</h3>
                <p className="text-sm text-gray-400 mb-2">
                  {t('auth.tokenSystemDesc')}
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-green-500/20 text-green-400 text-xs">
                    <Gift className="w-3 h-3" />
                    {t('auth.freeWelcomeTokens')}
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white/10 text-gray-300 text-xs">
                    <Check className="w-3 h-3" />
                    {t('auth.noSubscriptionNeeded')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-gray-300">{t('auth.name')}</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input 
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('auth.name')}
                  className="pl-10 bg-white/5 border-white/10 focus:border-[#D4AF37] text-white placeholder:text-gray-600"
                  required
                  data-testid="register-name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-300">{t('auth.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input 
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@email.com"
                  className="pl-10 bg-white/5 border-white/10 focus:border-[#D4AF37] text-white placeholder:text-gray-600"
                  required
                  data-testid="register-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-300">{t('auth.password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input 
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 pr-10 bg-white/5 border-white/10 focus:border-[#D4AF37] text-white placeholder:text-gray-600"
                  required
                  minLength={6}
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Age Verification - Fixed z-index issue */}
            <div className="flex items-start gap-3 relative z-10">
              <Checkbox 
                id="age"
                checked={ageVerified}
                onCheckedChange={setAgeVerified}
                className="mt-1 border-white/20 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                data-testid="age-checkbox"
              />
              <Label htmlFor="age" className="text-sm text-gray-400 cursor-pointer select-none">
                {t('auth.ageVerify')}
              </Label>
            </div>

            <Button 
              type="submit"
              disabled={loading || !ageVerified}
              className="w-full btn-gold py-6 text-lg disabled:opacity-50"
              data-testid="register-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  {t('auth.registering')}
                </span>
              ) : (
                t('auth.createAccount')
              )}
            </Button>
          </form>

          {/* Login Link */}
          <p className="mt-8 text-center text-gray-400">
            {t('auth.hasAccount')}{' '}
            <Link 
              to="/login" 
              className="text-[#D4AF37] hover:text-[#FCD34D] transition-colors"
              data-testid="login-link"
            >
              {t('auth.login')}
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img 
          src="https://customer-assets.emergentagent.com/job_model-subscription-1/artifacts/aivsx8qx_IMG_4315.jpeg"
          alt="Model"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-[#050505] via-transparent to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
      </div>
    </div>
  );
}
